import { addWeeks, startOfDay, subWeeks } from "date-fns";
import {
  FilterType,
  SearchFilter,
} from "../../../common/components/functional/FilterSearchField/FilterSearchField";
import isEmpty from "../../../common/helpers/isEmpty";

export const getDateRangeDefaultValues = (today: Date) => {
  const currentDate = startOfDay(new Date()); // Start of the current day
  const dateRangeStart = subWeeks(currentDate, 3); // 3 weeks ago from the current date
  const dateRangeEnd = addWeeks(currentDate, 3); // 3 weeks into the future from the current date

  return { startDate: dateRangeStart, endDate: dateRangeEnd };
};

const defaultDateRange = getDateRangeDefaultValues(new Date());
const startDateEpochMs = defaultDateRange.startDate.getTime();
const endDateEpochMs = defaultDateRange.endDate.getTime();

export const applyFilters = (filters: Array<{ [key: string]: any }>) => {
  const appliedFilters: { [key: string]: any } = {};
  filters.forEach((obj) => {
    const { name, value } = obj;
    if (value && !isEmpty(value)) {
      appliedFilters[name] = value;
      return appliedFilters;
    }
  });
  return { ...appliedFilters };
};

export const getInitialFilters = (talentId?: string): SearchFilter[] => {
  return [
    { name: "rangeStart", value: startDateEpochMs, type: "date" as FilterType },
    { name: "rangeEnd", value: endDateEpochMs, type: "date" as FilterType },
    { name: "paymentPeriod", value: "" },
    { name: "status", value: "" },
    { name: "customerOrgId", value: "", displayValue: "" },
    { name: "customerCompanyId", value: "", displayValue: "" },
    { name: "eezyCompanyId", value: "", displayValue: "" },
    { name: "isOnInvoice", value: "" },
    { name: "isOnPayslip", value: "" },
    { name: "approval", value: talentId ? "all" : "CustomerRejected" },
    { name: "isZeroExpenseEntry", value: "", displayValue: "" },
    { name: "tesId", value: "", displayName: "common:tes", displayValue: "" },
    {
      name: "hasComment",
      value: "",
      displayName: "talentProfile:comment",
      displayValue: "",
    },
    {
      name: "workLocationIds",
      value: [],
      displayName: "common:workLocation",
      displayValue: "",
    },
    {
      name: "hrRepresentativeIds",
      value: [],
      displayName: "talentProfile:myHr",
      displayValue: "",
    },
  ];
};

// Rejected (0) and Absence (1) tabs are defined by their approval and status.
// Stored filters may have other values (e.g. saved with the wrong tab), which
// would switch the tab to custom on the next filter change, so reset those two
// to the tab's own values and keep all other stored filters (e.g. date range).
const TAB_DEFINING_FILTERS: Record<0 | 1, Record<string, string>> = {
  0: { approval: "CustomerRejected", status: "" },
  1: { approval: "New", status: "absence" },
};

export const withTabDefiningFilters = (
  filters: SearchFilter[],
  tab: 0 | 1,
): SearchFilter[] =>
  filters.map((filter) =>
    filter.name in TAB_DEFINING_FILTERS[tab]
      ? { ...filter, value: TAB_DEFINING_FILTERS[tab][filter.name] }
      : filter,
  );

// Stored filters may have an empty date range. Fill it with the default range,
// but keep a range the user has set.
export const withDefaultDateRangeIfEmpty = (
  filters: SearchFilter[],
): SearchFilter[] =>
  filters.map((filter) => {
    if (filter.name === "rangeStart" && !filter.value) {
      return { ...filter, value: startDateEpochMs };
    }
    if (filter.name === "rangeEnd" && !filter.value) {
      return { ...filter, value: endDateEpochMs };
    }
    return filter;
  });
